import { useAllSupportGrants, useTenants, useRevokeAnySupportGrant } from '@/api/admin'
import type { SupportGrant, Tenant, SupportGrantSection } from '@/types'

const ALL_SECTIONS: { value: SupportGrantSection; label: string }[] = [
  { value: 'devices',     label: 'Devices' },
  { value: 'fleets',      label: 'Fleets' },
  { value: 'credentials', label: 'SSH Credentials' },
  { value: 'ota_updates', label: 'OTA Updates' },
  { value: 'playbooks',   label: 'Playbooks' },
]

function sectionLabel(s: SupportGrantSection) {
  return ALL_SECTIONS.find(x => x.value === s)?.label ?? s
}

function grantStatus(g: SupportGrant): 'active' | 'expired' | 'revoked' {
  if (g.revoked_at) return 'revoked'
  if (g.expires_at && new Date(g.expires_at) < new Date()) return 'expired'
  return 'active'
}

interface GrantRowProps {
  grant: SupportGrant
  tenantName: string
  tenantSlug: string
  onRevoke: (tenantId: string, grantId: string) => void
  revoking: boolean
}

function GrantRow({ grant, tenantName, tenantSlug, onRevoke, revoking }: GrantRowProps) {
  const status = grantStatus(grant)

  const statusBadge: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', gap: 5,
    padding: '2px 9px', borderRadius: 3, fontSize: 11, fontWeight: 600,
    ...(status === 'active'
      ? { background: 'rgba(34,197,94,0.12)', color: '#4ade80', border: '1px solid rgba(34,197,94,0.3)' }
      : status === 'expired'
        ? { background: 'rgba(234,179,8,0.12)', color: '#facc15', border: '1px solid rgba(234,179,8,0.3)' }
        : { background: 'rgba(100,116,139,0.15)', color: 'var(--color-text-muted)', border: '1px solid var(--color-border)' })
  }

  return (
    <tr style={{ opacity: status !== 'active' ? 0.55 : 1 }}>
      <td style={{ padding: '10px 0', paddingRight: 16 }}>
        <div style={{ fontWeight: 600, fontSize: 13 }}>{tenantName}</div>
        <code style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{tenantSlug}</code>
      </td>
      <td style={{ padding: '10px 0', paddingRight: 16 }}>
        <span style={statusBadge}>● {status}</span>
      </td>
      <td style={{ padding: '10px 0', paddingRight: 16 }}>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {grant.sections.map(s => (
            <span key={s} style={{
              fontSize: 11, padding: '2px 7px', borderRadius: 3,
              background: status === 'active' ? 'var(--color-accent-dim)' : 'var(--color-surface-2)',
              color: status === 'active' ? 'var(--color-accent)' : 'var(--color-text-muted)',
              border: `1px solid ${status === 'active' ? 'rgba(0,229,182,0.25)' : 'var(--color-border)'}`,
            }}>
              {sectionLabel(s)}
            </span>
          ))}
        </div>
      </td>
      <td style={{ padding: '10px 0', paddingRight: 16, fontSize: 12, color: 'var(--color-text-muted)', maxWidth: 220 }}>
        {grant.note ?? '—'}
      </td>
      <td style={{ padding: '10px 0', paddingRight: 16, fontSize: 12, color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>
        {new Date(grant.created_at).toLocaleString()}
      </td>
      <td style={{ padding: '10px 0', paddingRight: 16, fontSize: 12, color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>
        {grant.expires_at ? new Date(grant.expires_at).toLocaleString() : 'Until revoked'}
      </td>
      <td style={{ padding: '10px 0', textAlign: 'right' }}>
        {status === 'active' && (
          <button
            className="btn btn-ghost"
            style={{ padding: '3px 10px', fontSize: 11, color: '#ef4444' }}
            disabled={revoking}
            onClick={() => {
              if (confirm(`Revoke support access for tenant "${tenantName}"?`)) {
                onRevoke(grant.tenant_id, grant.id)
              }
            }}
          >
            Revoke
          </button>
        )}
      </td>
    </tr>
  )
}

function GrantTable({ grants, tenantMap, onRevoke, revoking }: {
  grants: SupportGrant[]
  tenantMap: Record<string, Tenant>
  onRevoke: (tenantId: string, grantId: string) => void
  revoking: boolean
}) {
  const thStyle: React.CSSProperties = {
    textAlign: 'left', fontSize: 11, fontWeight: 600, textTransform: 'uppercase' as const,
    letterSpacing: '0.06em', color: 'var(--color-text-muted)',
    padding: '6px 0', paddingRight: 16, borderBottom: '1px solid var(--color-border)',
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={thStyle}>Tenant</th>
            <th style={thStyle}>Status</th>
            <th style={thStyle}>Sections</th>
            <th style={thStyle}>Note</th>
            <th style={thStyle}>Granted at</th>
            <th style={thStyle}>Expires</th>
            <th style={{ ...thStyle, textAlign: 'right' }}></th>
          </tr>
        </thead>
        <tbody>
          {grants.map(g => {
            const tenant = tenantMap[g.tenant_id]
            return (
              <GrantRow
                key={g.id}
                grant={g}
                tenantName={tenant?.name ?? g.tenant_id.slice(0, 8) + '…'}
                tenantSlug={tenant?.slug ?? ''}
                onRevoke={onRevoke}
                revoking={revoking}
              />
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export function SupportRequests() {
  const { data: grants, isLoading: grantsLoading } = useAllSupportGrants()
  const { data: tenantsData } = useTenants()
  const revokeMutation = useRevokeAnySupportGrant()

  const tenantMap: Record<string, Tenant> = Object.fromEntries(
    (tenantsData?.data ?? []).map(t => [t.id, t])
  )

  const allGrants = grants ?? []
  const activeGrants = allGrants.filter(g => grantStatus(g) === 'active')
  const inactiveGrants = allGrants.filter(g => grantStatus(g) !== 'active')

  const handleRevoke = (tenantId: string, grantId: string) => {
    revokeMutation.mutate({ tenantId, grantId })
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Support Requests</h1>
          <p className="page-subtitle">
            Tenant-admin grants — sections you have been authorized to access
          </p>
        </div>
      </div>

      {/* Active grants */}
      <div style={{
        background: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius)',
        padding: 20,
        marginBottom: 24,
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: 16,
        }}>
          <div style={{
            fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
            letterSpacing: '0.06em', color: 'var(--color-text-muted)',
          }}>
            Active grants
          </div>
          {!grantsLoading && (
            <span style={{
              fontSize: 11, fontWeight: 600, padding: '2px 9px', borderRadius: 3,
              background: activeGrants.length > 0 ? 'rgba(34,197,94,0.12)' : 'var(--color-surface-2)',
              color: activeGrants.length > 0 ? '#4ade80' : 'var(--color-text-muted)',
              border: `1px solid ${activeGrants.length > 0 ? 'rgba(34,197,94,0.3)' : 'var(--color-border)'}`,
            }}>
              {activeGrants.length} active
            </span>
          )}
        </div>

        {grantsLoading && (
          <div style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>Loading…</div>
        )}

        {!grantsLoading && activeGrants.length === 0 && (
          <div style={{ fontSize: 13, color: 'var(--color-text-muted)', padding: '16px 0' }}>
            No active support grants. Tenant-admins can grant access via <strong>Tenant Status → Platform Support Access</strong>.
          </div>
        )}

        {activeGrants.length > 0 && (
          <GrantTable
            grants={activeGrants}
            tenantMap={tenantMap}
            onRevoke={handleRevoke}
            revoking={revokeMutation.isPending}
          />
        )}
      </div>

      {/* History — expired & revoked */}
      {inactiveGrants.length > 0 && (
        <details style={{
          background: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius)',
          padding: 20,
        }}>
          <summary style={{
            fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
            letterSpacing: '0.06em', color: 'var(--color-text-muted)',
            cursor: 'pointer', userSelect: 'none',
          }}>
            History ({inactiveGrants.length} expired / revoked)
          </summary>
          <div style={{ marginTop: 16 }}>
            <GrantTable
              grants={inactiveGrants}
              tenantMap={tenantMap}
              onRevoke={handleRevoke}
              revoking={false}
            />
          </div>
        </details>
      )}
    </div>
  )
}

